// ticket-files-at-head.ts
//
// Orchestrator recovery (3.40.0, review G2 M2): recovery reads the committed
// tickets only — the PLAN.md it commits, the next free id and the source
// ticket's kind all come from HEAD, never from bytes a worker left in the
// worktree (the materializer also runs at tick start, and after a post-route
// reset that may have been skipped).
//
// Security audit L-3: only regular-file blobs (mode 100644/100755), read by
// object id with `cat-file blob`, each at most MAX_TICKET_BLOB_BYTES. Anything
// else throws a coded error — fail closed: the materializer reports it and
// retries next tick. Skipping instead would drop that file's id from the
// next-free-id pool, and writeTicketFile would then delete it as a sibling.
import type { GitRunner } from "./commit-owner-git-ops.js";
import { type Ticket, TicketSchema } from "./schemas/ticket-schema.js";

const TICKETS_DIR = ".avatar/tickets";
const REGULAR_FILE_MODES = new Set(["100644", "100755"]);
export const MAX_TICKET_BLOB_BYTES = 256 * 1024;

async function gitStdout(gitRunner: GitRunner, args: string[], repoDir: string): Promise<string> {
  const res = await gitRunner(args, repoDir);
  if (res.status !== 0) throw new Error(`git ${args.join(" ")} thất bại: ${res.stderr.trim()}`);
  return res.stdout;
}

interface TreeEntry {
  mode: string;
  type: string;
  oid: string;
  size: string;
  path: string;
}

/** `ls-tree -l -z` record: `<mode> SP <type> SP <oid> SP+ <size> TAB <path>`. */
function parseTreeEntry(record: string): TreeEntry | undefined {
  const match = /^(\d+) (\w+) ([0-9a-f]+) +(\S+)\t(.+)$/s.exec(record);
  if (match === null) return undefined;
  const [, mode = "", type = "", oid = "", size = "", path = ""] = match;
  return { mode, type, oid, size, path };
}

/** Every `.avatar/tickets/*.json` tracked at HEAD, parsed, sorted by path. */
export async function loadTicketsAtHead(gitRunner: GitRunner, repoDir: string): Promise<Ticket[]> {
  // -z: no C-quoting of non-ASCII names; --full-name: repo-relative paths; -l: blob size.
  const listing = await gitStdout(
    gitRunner,
    ["ls-tree", "-z", "-l", "--full-name", "HEAD", "--", `${TICKETS_DIR}/`],
    repoDir,
  );
  const entries = listing
    .split("\0")
    .map(parseTreeEntry)
    .filter((e): e is TreeEntry => e?.path.endsWith(".json") === true)
    .sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  const tickets: Ticket[] = [];
  for (const entry of entries) {
    if (entry.type !== "blob" || !REGULAR_FILE_MODES.has(entry.mode)) {
      throw new Error(`ticket_not_regular_file: ${entry.path} (mode ${entry.mode}) ở HEAD`);
    }
    if (!(Number(entry.size) <= MAX_TICKET_BLOB_BYTES)) {
      throw new Error(`ticket_too_large: ${entry.path} (${entry.size} byte) ở HEAD`);
    }
    const raw = await gitStdout(gitRunner, ["cat-file", "blob", entry.oid], repoDir);
    tickets.push(TicketSchema.parse(JSON.parse(raw)));
  }
  return tickets;
}
